import * as React from "react"
import { Input as InputPrimitive } from "@base-ui/react/input"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/primitives/utils"

const inputVariants = cva(
  "w-full min-w-0 transition-[color,background-color,border-color,box-shadow] duration-100 ease-snappy outline-none file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-xs file:font-medium file:text-foreground placeholder:text-subtle-foreground disabled:pointer-events-none disabled:cursor-not-allowed",
  {
    variants: {
      // beacon: a 32px field on the panel's own surface (sunken in dark), a hairline that firms on
      // hover, and a soft 3px halo in the ring colour on focus; invalid swaps both for destructive.
      // `bare` has no chrome at all and inherits its font size, for a title typed in place (the
      // composer, inline edits, the command palette), which style its size and weight themselves.
      variant: {
        default:
          "h-8 rounded-md border border-input bg-background px-2.5 text-ui shadow-none hover:border-border-strong focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/20 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/15 disabled:bg-muted disabled:text-muted-foreground dark:bg-surface-sunken dark:disabled:bg-muted",
        bare: "h-auto rounded-md border-0 bg-transparent px-0 shadow-none ring-0 focus-visible:ring-0 disabled:text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

function Input({
  className,
  type,
  variant = "default",
  ...props
}: React.ComponentProps<"input"> & VariantProps<typeof inputVariants>) {
  return (
    <InputPrimitive
      type={type}
      data-slot="input"
      data-variant={variant}
      className={cn(inputVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Input, inputVariants }
